import { Check, Globe, Clapperboard } from "lucide-react";
import { Reveal, SectionTag } from "./Reveal";
import { BookCallButton, ArrowLink } from "./CTA";

const WEBSITE_TIERS = [
  {
    name: "Basic Website",
    price: "₹20,000",
    per: "one-time",
    features: [
      "Professional homepage",
      "About page",
      "Hajj page",
      "Umrah page",
      "Packages section",
      "Contact page",
      "FAQ section",
      "Testimonials section",
      "WhatsApp CTA",
      "Enquiry form",
      "Mobile responsive design",
      "Basic SEO foundations",
      "Basic analytics setup",
    ],
    featured: false,
    testid: "website-basic",
  },
  {
    name: "Professional Website",
    price: "₹40,000",
    per: "one-time",
    features: [
      "Everything in Basic",
      "Improved conversion structure",
      "Dedicated Hajj and Umrah sections",
      "Enhanced package presentation",
      "Stronger CTA placement",
      "Trust-building sections",
      "Improved enquiry flow",
      "WhatsApp integration",
      "Analytics configuration",
      "Performance optimization",
      "Advanced page structure",
    ],
    featured: true,
    testid: "website-professional",
  },
  {
    name: "Advanced Website",
    price: "₹50,000",
    per: "one-time",
    features: [
      "Everything in Professional",
      "Conversion-focused landing-page structure",
      "Dedicated Hajj campaign page",
      "Dedicated Umrah campaign page",
      "Campaign-ready sections",
      "Advanced enquiry forms",
      "Conversion tracking",
      "Meta Pixel integration",
      "Enhanced WhatsApp lead flow",
      "Advanced package presentation",
      "Advanced conversion architecture",
      "Analytics and performance configuration",
      "Paid-traffic-ready structure",
    ],
    featured: false,
    testid: "website-advanced",
  },
];

const CONTENT_SUPPORTS = [
  "Instagram",
  "Facebook",
  "Meta Ads",
  "Brand awareness",
  "Package promotion",
  "Trust",
  "Lead generation",
];

export const OneTimeServices = () => (
  <section className="bg-cream py-24 md:py-32 relative overflow-hidden border-t border-charcoal/10" data-testid="one-time-services">
    <div className="absolute inset-0 geo-pattern opacity-[0.04]" />
    <div className="relative max-w-7xl mx-auto px-6">
      <Reveal className="mb-16 max-w-3xl">
        <SectionTag className="mb-6">One-Time Services</SectionTag>
        <h2 className="text-3xl md:text-5xl font-bold tracking-tighter text-charcoal leading-[1.05]">
          Website Development, Built to Turn Visitors Into Enquiries.
        </h2>
        <p className="mt-5 text-base text-charcoal/65 leading-relaxed">
          A separate one-time service category for agencies that need a
          professional website before (or alongside) monthly marketing.{" "}
          <ArrowLink to="/services/website-development" testid="one-time-website-link">
            Explore Website Development
          </ArrowLink>
        </p>
      </Reveal>

      <div className="grid md:grid-cols-3 gap-6">
        {WEBSITE_TIERS.map((tier, i) => (
          <Reveal key={tier.name} delay={i * 0.1} className="h-full">
            <div
              data-testid={tier.testid}
              className={`h-full flex flex-col p-8 md:p-10 border transition-colors duration-300 ${
                tier.featured
                  ? "bg-charcoal text-cream border-gold/50 shadow-[0_24px_60px_rgba(45,42,39,0.25)]"
                  : "bg-white text-charcoal border-charcoal/10 hover:border-gold/50"
              }`}
            >
              <div className="flex items-center justify-between">
                <div
                  className={`text-xs font-semibold tracking-[0.25em] uppercase ${
                    tier.featured ? "text-gold" : "text-tan"
                  }`}
                >
                  {tier.name}
                </div>
                <Globe size={18} strokeWidth={1.5} className={tier.featured ? "text-gold" : "text-tan"} />
              </div>
              <div className="mt-6 flex items-baseline gap-2">
                <span className="text-4xl md:text-5xl font-bold tracking-tighter">
                  {tier.price}
                </span>
                <span className={`text-sm ${tier.featured ? "text-cream/50" : "text-charcoal/50"}`}>
                  {tier.per}
                </span>
              </div>
              <ul className="mt-8 space-y-3 flex-1">
                {tier.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm">
                    <Check
                      size={15}
                      strokeWidth={2}
                      className={`mt-0.5 shrink-0 ${tier.featured ? "text-gold" : "text-tan"}`}
                    />
                    <span className={tier.featured ? "text-cream/80" : "text-charcoal/75"}>
                      {f}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="mt-10">
                <BookCallButton
                  label="Discuss This Plan"
                  testid={`${tier.testid}-cta`}
                  dark={tier.featured}
                  className="w-full"
                />
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.2}>
        <div
          className="mt-6 bg-charcoal text-cream border border-gold/40 p-8 md:p-12 grid lg:grid-cols-12 gap-10"
          data-testid="content-day-card"
        >
          <div className="lg:col-span-5">
            <div className="flex items-center justify-between">
              <div className="text-xs font-semibold tracking-[0.25em] uppercase text-gold">
                On-Ground Content Day
              </div>
              <Clapperboard size={18} strokeWidth={1.5} className="text-gold" />
            </div>
            <div className="mt-6 flex items-baseline gap-2">
              <span className="text-4xl md:text-5xl font-bold tracking-tighter">₹30,000</span>
              <span className="text-sm text-cream/50">one-time</span>
            </div>
            <p className="mt-4 text-sm text-cream/65 leading-relaxed">
              One professional content production day for your agency,
              producing 10 reels your marketing can use immediately.
            </p>
            <div className="mt-8">
              <BookCallButton
                label="Discuss a Content Day"
                testid="content-day-cta"
                dark
              />
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="text-xs font-semibold tracking-[0.25em] uppercase text-gold mb-5">
              Content Designed to Support
            </div>
            <div className="flex flex-wrap gap-2">
              {CONTENT_SUPPORTS.map((c) => (
                <span
                  key={c}
                  className="border border-cream/20 bg-cream/5 px-3 py-1.5 text-[11px] font-medium text-cream/75"
                >
                  {c}
                </span>
              ))}
            </div>
            <p className="mt-6 text-xs text-cream/45 leading-relaxed border-t border-cream/10 pt-5">
              Includes 10 reels. Required locations, travel, production
              requirements and other agreed expenses are separate unless
              otherwise agreed.{" "}
              <ArrowLink to="/services/on-ground-content-production" testid="content-day-link" dark>
                Explore Content Production
              </ArrowLink>
            </p>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);
